import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import PdfPagesView from "@/components/PdfPagesView";
import { Download, Printer, Loader2, Hash, Type, AlignLeft, Eye, Percent, StickyNote, ScrollText } from "lucide-react";
import { buildLedgerPdf, LEDGER_PDF_DEFAULTS } from "@/lib/ledgerPdf";
import { printPdf } from "@/lib/quotePdf";
import { notesOf } from "@/components/ContactNotes";
import { flattenLedger, ledgerTotals } from "./ledgerUtils";
import { cn } from "@/lib/utils";
import { cariShort, docFileName } from "@/lib/format";
import { toast } from "sonner";

const fileName = (c) => docFileName("", `Cari Defter ${cariShort(c?.title) || ""}`);

function Opt({ on, onClick, icon: Icon, label, hint, testid, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={on} data-testid={testid}
      className={cn("w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed", on ? "border-[#0F2745]/60 bg-[#0F2745]/[0.05]" : "border-slate-200 bg-white hover:border-slate-300")}>
      <span className={cn("h-7 w-7 rounded-md flex items-center justify-center shrink-0 transition-colors", on ? "bg-[#0F2745] text-[#F2B705]" : "bg-slate-100 text-slate-400")}><Icon className="h-3.5 w-3.5" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-bold text-slate-800">{label}</span>
        {hint && <span className="block text-[10.5px] text-muted-foreground leading-snug">{hint}</span>}
      </span>
      <span className={cn("relative h-4 w-7 rounded-full shrink-0 transition-colors", on ? "bg-[#0F2745]" : "bg-slate-300")}>
        <span className={cn("absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white transition-transform", on ? "translate-x-3" : "translate-x-0")} />
      </span>
    </button>
  );
}

const Group = ({ title, children }) => (
  <div className="space-y-1.5">
    <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 font-head">{title}</div>
    {children}
  </div>
);

// Cari Defter PDF — ekrandaki önizleme, indirilen/yazdırılan PDF'in kendisidir (birebir)
export default function LedgerPreviewDialog({ open, onClose, contact: c, data }) {
  const [range, setRange] = useState({ start: "", end: "" });
  const [opts, setOpts] = useState(LEDGER_PDF_DEFAULTS);
  const [pdfData, setPdfData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pageW, setPageW] = useState(794);
  const pdfRef = useRef(null);
  const { rows, entries } = useMemo(() => flattenLedger(data?.cari_entries || [], range), [data, range]);
  const totals = useMemo(() => ledgerTotals(entries, data?.payments || [], range, !c?.vat_exempt), [entries, data, range, c]);
  const anyPending = rows.some((r) => r.status === "bekliyor");
  const notes = useMemo(() => notesOf(c), [c]);

  useEffect(() => { if (open) setOpts(LEDGER_PDF_DEFAULTS); }, [open]);   // her açılışta: ürün adı + fiyatlar açık
  useEffect(() => {
    const fit = () => setPageW(Math.max(300, Math.min(794, window.innerWidth - (window.innerWidth >= 1024 ? 440 : 48))));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);
  useEffect(() => {
    if (!open || !c) return undefined;
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const pdf = await buildLedgerPdf({ contact: c, rows, totals, range, notes, anyPending }, opts);
        if (cancelled) return;
        pdfRef.current = pdf;
        setPdfData(pdf.output("arraybuffer"));
      } catch (e) { console.error(e); if (!cancelled) toast.error("PDF önizlemesi oluşturulamadı"); }
      finally { if (!cancelled) setBusy(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, c, rows, totals, range, notes, anyPending, opts]);

  const toggle = (k) => {
    const n = { ...opts, [k]: !opts[k] };
    if (!n.code && !n.name && !n.desc) { toast.error("En az bir ürün bilgisi (kod, ad veya açıklama) açık kalmalı"); return; }
    setOpts(n);
  };
  const download = () => { pdfRef.current?.save(fileName(c)); toast.success("Cari defter PDF indirildi"); };
  const print = () => pdfRef.current && printPdf(pdfRef.current, fileName(c).replace(/\.pdf$/, ""), { asFileName: true });
  const ready = !busy && !!pdfData;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[1320px] w-[98vw] h-[94vh] max-h-[94vh] p-0 gap-0 overflow-hidden flex flex-col lg:flex-row" data-testid="ledger-preview">
        <aside className="lg:w-[360px] shrink-0 border-b lg:border-b-0 lg:border-r bg-white flex flex-col max-h-[48vh] lg:max-h-none">
          <div className="relative bg-[#0F2745] text-white px-5 py-4 pr-12">
            <span className="absolute left-0 right-0 bottom-0 h-[3px] bg-[#F2B705]" />
            <DialogTitle className="font-head text-lg flex items-center gap-2"><ScrollText className="h-5 w-5 text-[#F2B705]" />Cari Defter PDF</DialogTitle>
            <p className="text-xs text-slate-300 truncate mt-0.5" data-testid="ledger-preview-contact">{c?.title}</p>
            <p className="text-[11px] text-slate-400 mt-1" data-testid="ledger-preview-stats">{rows.length} kalem · {totals.payments?.length || 0} ödeme</p>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-5">
            <Group title="Dönem">
              <div className="grid grid-cols-2 gap-2" data-testid="ledger-range-bar">
                <div>
                  <Label className="text-[10px] text-muted-foreground">Başlangıç</Label>
                  <DatePicker value={range.start} onChange={(v) => setRange({ ...range, start: v })} placeholder="Başlangıç" className="h-9" data-testid="ledger-start" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Bitiş</Label>
                  <DatePicker value={range.end} onChange={(v) => setRange({ ...range, end: v })} placeholder="Bugün" className="h-9" data-testid="ledger-end" />
                </div>
              </div>
              {(range.start || range.end) ? <button type="button" onClick={() => setRange({ start: "", end: "" })} className="text-xs text-muted-foreground underline" data-testid="ledger-range-clear">Tüm kayıtlar</button> : null}
            </Group>
            <Group title="Ürün Bilgileri">
              <Opt on={opts.name} onClick={() => toggle("name")} icon={Type} label="Ürün adı" hint="Kapalıyken ürün adı yazılmaz" testid="ledger-opt-name" />
              <Opt on={opts.desc} onClick={() => toggle("desc")} icon={AlignLeft} label="Ürün açıklaması" hint="Ürün adının altındaki açıklama satırı" testid="ledger-opt-desc" />
              <Opt on={opts.code} onClick={() => toggle("code")} icon={Hash} label="Ürün kodu" hint="Ayrı sütun olarak" testid="ledger-opt-code" />
            </Group>
            <Group title="Tutarlar">
              <Opt on={opts.prices} onClick={() => toggle("prices")} icon={Eye} label="Fiyatları göster" hint={opts.prices ? "Fiyatlar, ödemeler ve toplamlar yazılır" : "Tüm tutarlar gizli — müşteri yalnızca aldığı ürünleri görür"} testid="ledger-opt-prices" />
              <Opt on={opts.prices && opts.detailed} disabled={!opts.prices} onClick={() => toggle("detailed")} icon={Percent} label="Liste fiyatı ve iskonto" hint="Kapalıyken yalnızca net birim fiyat" testid="ledger-opt-detailed" />
            </Group>
            {notes.length > 0 && (
              <Group title="Ek">
                <Opt on={opts.notes} onClick={() => toggle("notes")} icon={StickyNote} label={`Cari notları (${notes.length})`} hint="Notlar müşteriye giden PDF'e yazdırılır" testid="ledger-opt-notes" />
              </Group>
            )}
          </div>
          <div className="p-4 border-t bg-slate-50 flex gap-2">
            <Button className="flex-1 bg-[#0F2745] hover:bg-[#16345c]" onClick={download} disabled={!ready} data-testid="ledger-download-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}PDF İndir</Button>
            <Button variant="outline" className="flex-1" onClick={print} disabled={!ready} data-testid="ledger-print-btn"><Printer className="h-4 w-4 mr-1.5" />Yazdır</Button>
          </div>
        </aside>
        <div className="flex-1 min-h-0 overflow-auto bg-slate-200/70 p-4 lg:p-6" data-testid="ledger-preview-pages">
          {pdfData ? <PdfPagesView data={pdfData} width={pageW} testid="ledger-pdf-pages" /> : (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" />PDF hazırlanıyor…</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
